import {
  DialogContent,
  Box,
  Button,
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  DialogContentText,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import InsightsIcon from "@mui/icons-material/Insights";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Invoice from "./Invoice";
import { useState } from "react";

const PackageDetailSection = ({ isScrollable = false }) => {
  const [open, setOpen] = useState(false);
  const [enquiryPlan, setEnquiryPlan] = useState("");
  const loginData = JSON.parse(localStorage.getItem("loginDetails"));

  const handleClose = () => {
    setOpen(false);
  };

  const PLAN_ORDER = ["Starter Plan", "Growth Plan", "Pro Plan", "Enterprise Plan"];

  // Normalize backend value: accepts "Starter Plan", "starter plan", "Starter", "starter", etc.
  const normalizePlan = (raw) => {
    if (!raw) return "";
    const s = raw.trim().toLowerCase();
    if (s === "starter" || s === "starter plan") return "Starter Plan";
    if (s === "growth" || s === "growth plan") return "Growth Plan";
    if (s === "pro" || s === "pro plan") return "Pro Plan";
    if (s === "enterprise" || s === "enterprise plan") return "Enterprise Plan";
    return raw; // unrecognised — pass through so fallback (show all) applies
  };

  const planDetails = [
    {
      title: "Starter Plan",
      description: "Smart Report",
      highlights: ["Core smart report workflows", "Email support assistance"],
      idealFor: "New labs",
      icon: RocketLaunchIcon,
    },
    {
      title: "Growth Plan",
      description: "Smart Report & Add-ons",
      highlights: ["All Starter features included", "Higher usage ceiling"],
      idealFor: "Scaling labs",
      icon: InsightsIcon,
    },
    {
      title: "Pro Plan",
      description: "Smart Report & Advanced Add-ons",
      highlights: ["All Growth features included", "Advanced add-ons & reporting"],
      idealFor: "Scaling chains",
      icon: WorkspacePremiumIcon,
    },
    {
      title: "Enterprise Plan",
      description: "Smart Report with Viz App & Analytics",
      highlights: ["Viz App analytics included", "Custom workflows & integrations"],
      idealFor: "Multi-center teams",
      icon: CorporateFareIcon,
    },
  ];

  const currentPlanIndex = PLAN_ORDER.indexOf(normalizePlan(loginData?.subscription ?? ""));

  const renderCard = (each, isActive) => {
    const IconComp = each.icon;
    return (
      <Box
        key={each.title}
        sx={{
          flex: { xs: "none", md: "1 1 0" },
          minWidth: 180,
          borderRadius: "16px",
          p: "13px 13px 10px", // Remove bottom padding
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: isActive
            ? "linear-gradient(145deg, #3567D6 0%, #170C6B 100%)"
            : "#fff",
          border: isActive ? "none" : "1.5px solid #E8EEF8",
          boxShadow: isActive
            ? "0 12px 32px rgba(53,103,214,0.35)"
            : "0 2px 8px rgba(0,0,0,0.04)",
          transform: isActive ? "translateY(-4px) scale(1.02)" : "none",
          transition: "all 0.25s ease",
          height: "auto", // Ensure height fits content
        }}
      >
        {isActive && (
          <>
            <Box sx={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />
            <Box sx={{ position: "absolute", bottom: -20, right: 20, width: 70, height: 70, borderRadius: "50%", background: "rgba(60,208,255,0.1)", pointerEvents: "none" }} />
          </>
        )}
        {isActive && (
          <Stack direction="row" alignItems="center" gap={0.45} sx={{ alignSelf: "flex-start", width: "fit-content", display: "inline-flex", background: "rgba(255,255,255,0.18)", borderRadius: "14px", px: 0.9, py: 0.25, mb: 0.8 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: "#22C55E" }} />
            <Typography sx={{ color: "#fff", fontSize: "8.8px", fontWeight: 700, letterSpacing: "0.3px", lineHeight: 1.1 }}>ACTIVE</Typography>
          </Stack>
        )}
        <Box sx={{ mb: 0.8 }}><IconComp sx={{ fontSize: 20, color: isActive ? "#3CD0FF" : "#3567D6" }} /></Box>
        <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: isActive ? "#fff" : "#170C6B", mb: 0.5 }}>
          {each.title}
        </Typography>
        <Typography sx={{ fontSize: "10.5px", color: isActive ? "rgba(255,255,255,0.7)" : "#9DA3AE", mb: 1, lineHeight: 1.35 }}>
          {each.description}
        </Typography>
        <Box sx={{ height: "1px", background: isActive ? "rgba(255,255,255,0.18)" : "#F0F4FF", mb: 1 }} />
        {isActive ? (
          <Stack gap={0.65}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "10px" }}>Started</Typography>
              <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: 600 }}>{formatDate(loginData?.startDate)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "10px" }}>Ending</Typography>
              <Typography sx={{ color: "#3CD0FF", fontSize: "10px", fontWeight: 700 }}>{formatDate(loginData?.endDate)}</Typography>
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack gap={0.5} mb={1}>
              {each.highlights?.map((item) => (
                <Stack key={item} direction="row" alignItems="center" gap={0.75}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", background: "#3567D6", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "9.8px", color: "#5D6A82", lineHeight: 1.25 }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: "9px", color: "#9DA3AE", textTransform: "uppercase", letterSpacing: "0.35px" }}>
                Ideal For
              </Typography>
              <Typography sx={{ fontSize: "10px", color: "#170C6B", fontWeight: 700 }}>
                {each.idealFor}
              </Typography>
            </Stack>
            <Box sx={{ mt: "auto" }}>
              <Button
                size="small"
                onClick={() => { setOpen(true); setEnquiryPlan(each.title); }}
                sx={{
                  borderRadius: "9px",
                  border: "1.5px solid #D0DEFF",
                  color: "#3567D6",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.45,
                  background: "#f0f4ff",
                  "&:hover": { background: "#e0eaff", border: "1.5px solid #3567D6" },
                }}
              >
                Upgrade <ArrowForwardIcon sx={{ fontSize: 12, ml: 0.2 }} />
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  };

  const enquiryModal = () => {
    const prefixedMessageUrl = `https://wa.me/917996604381?text=Hello,%20I%20would%20like%20more%20information%20regarding%20the%20${enquiryPlan}.`;
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {" "}
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h4">Plan Enquiry</Typography>
        </DialogTitle>
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Stack gap={2}>
            <Typography variant="body5">
              This will post an enquiry regarding the details of {enquiryPlan}{" "}
              to one of our executives.
            </Typography>
            <a href={prefixedMessageUrl} target="_blank">
              <Button sx={{ alignSelf: "flex-start" }}>Click to proceed</Button>
            </a>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

  const formatDate = (date) => {
    const modifiedDate = new Date(date);
    const day =
      modifiedDate.getDate() < 10
        ? `0${modifiedDate.getDate()}`
        : modifiedDate.getDate();
    const month =
      modifiedDate.getMonth() + 1 < 10
        ? `0${modifiedDate.getMonth() + 1}`
        : modifiedDate.getMonth() + 1;
    const requiredFormat = `${day}-${month}-${modifiedDate.getFullYear()}`;
    return requiredFormat;
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "16px", p: 2.25, boxShadow: "0 2px 16px rgba(53,103,214,0.08)", display: "flex", flexDirection: "column", boxSizing: "border-box", width: "100%", overflowX: "auto" }}>
      {/* ── Header: title row ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Stack gap={0.2}>
          <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#170C6B", lineHeight: 1.3 }}>Choose Your Plan</Typography>
          <Typography sx={{ color: "#9DA3AE", fontSize: "11px" }}>Upgrade anytime as your lab grows</Typography>
        </Stack>
        {loginData && loginData.role === "lab" ? <Invoice tone="light" /> : ""}
      </Stack>

      {/* ── Plans List ── */}
      <Stack direction={{ xs: "column", md: "row" }} gap={2} flexWrap="wrap" justifyContent="flex-start">
        {planDetails.map((each, idx) => {
          // If a plan is active, don't show lower-tier plans
          if (currentPlanIndex !== -1 && idx < currentPlanIndex) {
            return null;
          }
          const isActive = idx === currentPlanIndex;
          return renderCard(each, isActive);
        })}
      </Stack>

      {enquiryModal()}
    </Box>
  );
};

export default PackageDetailSection;
